import MDEditor, { commands } from '@uiw/react-md-editor';
import { useHistory } from 'react-router';
import rehypeSanitize from 'rehype-sanitize';
import { FormInput } from '../../components/Forms/FormInput.js';
import { Header } from '../../components/Header.js';
import { IconButton } from '../../components/IconButton.js';
import {
	IconArrowBack,
	IconCheck,
	IconEdit,
	IconEye,
	IconFeedback,
	IconPen,
	IconShare
} from '../../components/QuirkIcons/index.js';
import { QuirkIonContent } from '../../components/QuirkIonContent.js';
import { QuirkIonPage } from '../../components/QuirkIonPage.js';
import { FormValidationRules, useFormValues } from '../../hooks/useFormValues.js';
import { IonPage, IonToast, useIonModal } from '@ionic/react';
import { Post } from 'model';
import { useEffect, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useRecentPostsQuery } from '../../api/PostApi.js';
import { useSendEmailBlastMutation, useSuggestEmailBlastSubjectMutation } from '../../api/SiteSettingsAPI.js';
import { Collapsible } from '../../components/Collapsible.js';
import { EmailCard } from '../../components/EmailCard.js';
import { EmailPreview } from '../../components/EmailPreview.js';
import FormFooter from '../../components/FormFooter.js';
import { FormErrors } from '../../components/Forms/FormErrors.js';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from '../../components/Forms/FormFields.module.css';
import { PostTypeView } from '../../components/PostTypeView/PostTypeView.js';
import { QuirkLoading } from '../../components/QuirkLoading.js';
import { getBackendURL, useBackendDomain } from '../../hooks/useBackendDomain.js';
import { usePreferences } from '../../hooks/usePreferences.js';
import { EmailBlast, EmptyEmailBlast } from '../../model/EmailBlast.js';
import { Confirmation } from '../Confirmation.js';
import styles from './SendEmailBlast.module.css';

const EmailBlastRules: FormValidationRules<EmailBlast> = {
	subject: {
		rules: {
			required: {
				value: true,
				message: 'Subject is required'
			},
			maxLength: {
				value: 200,
				message: 'Must be less than {value} characters'
			}
		}
	},
	body: {
		rules: {
			maxLength: {
				value: 4000,
				message: 'Must be less than {value} characters'
			},
			minLength: {
				value: 20,
				message: 'Must be more than {value} characters'
			}
		}
	}
};

const PostLinkModal = ({ onDismiss }: { onDismiss: (selection?: Post | null) => void }) => {
	const { data: posts, isFetching } = useRecentPostsQuery({ postType: 'all', daysBack: 30 });
	return (
		<IonPage>
			<Header
				title="Select a post"
				left={<IconButton icon={<IconArrowBack />} onClick={() => onDismiss(null)} />}
			/>
			<QuirkIonContent>
				<Virtuoso
					data={posts || []}
					itemContent={(_, post) => (
						<div style={{ position: 'relative', padding: 20 }}>
							<div
								style={{
									position: 'absolute',
									top: 0,
									right: 0,
									bottom: 0,
									left: 0,
									zIndex: 2 // Ensure it's above the child content
								}}
								onClick={() => onDismiss(post)}
							/>
							<div style={{ position: 'relative', zIndex: 1 }}>
								<PostTypeView post={post} preview={true} />
							</div>
						</div>
					)}
				/>
			</QuirkIonContent>
			<QuirkLoading isLoading={isFetching} message="Loading recent posts" />
		</IonPage>
	);
};

export const SendEmailBlast = () => {
	const history = useHistory();
	const [trigger, results] = useSendEmailBlastMutation();
	const [triggerTest, resultsTest] = useSendEmailBlastMutation();
	const [triggerSuggest, resultsSuggest] = useSuggestEmailBlastSubjectMutation();
	const {
		setValues,
		formSuccess,
		formValues,
		validateForm,
		stateDataObject: emailBlast
	} = useFormValues<EmailBlast>(EmailBlastRules, EmptyEmailBlast);
	const [showPreview, setShowPreview] = useState(false);
	const [domain] = useBackendDomain();
	const apiRef = useRef(null);

	const [emailBlastBody, setEmailBlastBody] = usePreferences('emailBlastBody');
	const [emailBlastSubject, setEmailBlastSubject] = usePreferences('emailBlastSubject');

	useEffect(() => {
		if (!formValues?.body?.value && emailBlastBody) {
			setValues(current => ({ subject: current?.subject?.value, body: emailBlastBody }));
		}
		if (!formValues?.subject?.value && emailBlastSubject) {
			setValues(current => ({ subject: emailBlastSubject, body: current?.body?.value }));
		}
	}, [emailBlastBody, formValues?.body?.value, setValues, emailBlastSubject, formValues?.subject?.value]);

	const [present, dismiss] = useIonModal(Confirmation, {
		onDismiss: () => dismiss(),
		action: () => {
			onSubmit();
			dismiss();
		},
		actionLabel: 'Send',
		dismissLabel: 'Cancel',
		content: <h2>Are you sure you want to send this email to all subscribers?</h2>
	});

	const postCommand = {
		name: 'postLink',
		keyCommand: 'postLink',
		buttonProps: { 'aria-label': 'Insert Link to Post' },
		icon: <IconShare size={12} />,
		execute: (state: any, api: any) => {
			apiRef.current = api;
			showPostLinkModal();
		}
	};
	const [showPostLinkModal, dismissModal] = useIonModal(PostLinkModal, {
		onDismiss: (selection?: Post | null) => {
			if (selection) {
				showPostLinkModal();
				const newMarkdownString = `[${selection.title || 'Create a title'}](${getBackendURL(
					`/api/sharing/${selection._id})`,
					domain || window.location.origin
				)}`;
				(apiRef?.current as any)?.replaceSelection(newMarkdownString);
			}
			dismissModal();
		}
	});

	const handlePreview = () => {
		setShowPreview(!showPreview);
	};

	const handleSuggestSubject = async () => {
		if (formValues && formValues.body) {
			triggerSuggest({ body: formValues.body.value, subject: formValues.subject?.value || '' });
		}
	};
	useEffect(() => {
		if (resultsSuggest.isSuccess) {
			setValues(current => ({ subject: resultsSuggest.data.response || '', body: current?.body?.value }));
			setEmailBlastSubject(resultsSuggest.data.response || '');
		}
	}, [resultsSuggest.isSuccess, resultsSuggest.data, setValues, setEmailBlastSubject]);

	const onSubmit = async () => {
		const compiledNoErrors = validateForm();
		if (compiledNoErrors && formValues && formValues.subject && formValues.body) {
			const res = await trigger({ subject: formValues.subject.value, body: formValues.body.value, test: false });
			if (!('error' in res)) {
				setEmailBlastBody('');
				setEmailBlastSubject('');
			}
		}
	};

	const onTestSubmit = async () => {
		if (formValues && formValues.subject && formValues.body) {
			triggerTest({ subject: formValues.subject.value, body: formValues.body.value, test: true });
		}
	};
	return (
		<QuirkIonPage name="Edit Site Settings">
			<>
				<Header
					title="Send Email Blast"
					left={
						<IconButton
							icon={<IconArrowBack />}
							onClick={() => {
								history.go(-1);
							}}
						/>
					}
					right={
						<IconButton
							label="Send"
							fill="solid"
							disabled={!formSuccess || results.isSuccess}
							icon={<IconCheck />}
							onClick={() => onSubmit()}
						/>
					}
				/>
				<QuirkIonContent flush padding>
					<>
						<div className={formStyles.fieldsContainer}>
							{!results.isSuccess && (
								<>
									<Collapsible collapsed={!showPreview}>
										<EmailPreview emailBlast={emailBlast} />
										<div className={styles.previewFooter}>
											<IconButton label="Edit" icon={<IconPen />} onClick={handlePreview} />
										</div>
									</Collapsible>
									<Collapsible heightPadding={1000} collapsed={showPreview}>
										<FormInput
											id="subject"
											setValues={setValues}
											onChange={e => setEmailBlastSubject(e.target.value)}
											field={formValues?.subject}
											label="Subject"
											testId="editBlastSubjectField"
										/>

										<div className="container" style={{ height: 350 }}>
											<MDEditor
												preview="edit"
												height={300}
												value={formValues?.body?.value || ''}
												commandsFilter={command => {
													if (command.name === 'live' || command.name === 'fullscreen')
														return false;
													return command;
												}}
												commands={[
													// Custom Toolbars
													postCommand,
													commands.divider,
													commands.group(
														[
															commands.title1,
															commands.title2,
															commands.title3,
															commands.title4
														],
														{
															groupName: 'title',
															parent: commands.title,
															keyCommand: 'title',
															name: 'title',
															buttonProps: { 'aria-label': 'Insert Title' }
														}
													),

													commands.bold,
													commands.italic,
													commands.unorderedListCommand,
													commands.orderedListCommand,
													commands.link,
													commands.image,
													commands.quote,
													commands.code
												]}
												previewOptions={{
													rehypePlugins: [[rehypeSanitize]]
												}}
												onChange={value => {
													setValues(current => ({
														subject: current?.subject?.value,
														body: value
													}));
													setEmailBlastBody(value || '');
												}}
											/>
											<FormErrors errors={formValues?.body?.errors} />
										</div>

										<div className={styles.editFooter}>
											<IconButton label="Preview" icon={<IconEye />} onClick={handlePreview} />
											<IconButton
												label="Suggest Subject"
												icon={<IconEdit />}
												onClick={handleSuggestSubject}
											/>
											<IconButton
												label="Test Email"
												icon={<IconFeedback />}
												onClick={onTestSubmit}
											/>
										</div>
									</Collapsible>
								</>
							)}
							<Collapsible collapsed={!results.isSuccess}>
								<EmailCard
									header={`Success`}
									message="Email blast sent!"
									testId="emailBlastSuccessCard"
								/>
							</Collapsible>

							<FormFooter
								onCancel={() => {
									history.go(-1);
								}}
								onSubmit={() => {
									const compiledNoErrors = validateForm();
									if (compiledNoErrors && formValues && formValues.subject && formValues.body) {
										present();
									}
								}}
								submitLabel="Send"
								cancelIcon={<IconArrowBack />}
								cancelLabel="Back"
								disableSubmit={!formSuccess || results.isSuccess}
							/>
						</div>
						<QuirkLoading isLoading={results.isLoading} message="Sending emails" />
						<QuirkLoading
							isLoading={resultsSuggest.isLoading}
							message="Communing with the AI oracle to suggest a subject line"
						/>
						<QuirkLoading isLoading={resultsTest.isLoading} message="Sending test email to you" />
					</>
				</QuirkIonContent>
			</>
			<IonToast
				isOpen={results.isSuccess}
				color="success"
				position="top"
				message="Email Blast Sent!"
				duration={2000}
			/>

			<IonToast
				isOpen={resultsTest.isSuccess}
				color="success"
				position="top"
				message="Test Email Blast Sent!"
				duration={2000}
			/>
			<IonToast
				isOpen={results.isError}
				message={`There was a problem sending the email blast: ${(results?.error as any)?.data}`}
				color="danger"
				position="top"
				duration={2000}
			/>
			<IonToast
				isOpen={resultsSuggest.isError}
				message={`There was a problem getting a suggested subject: ${(resultsSuggest?.error as any)?.data}`}
				color="danger"
				position="top"
				duration={2000}
			/>

			<IonToast
				isOpen={resultsTest.isError}
				message={`There was a problem sending the test email blast: ${(resultsTest?.error as any)?.data}`}
				color="danger"
				position="top"
				duration={2000}
			/>
		</QuirkIonPage>
	);
};
